import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { recoverAdminSession } from '../utils/sessionRecovery';
import type {
  SystemOverviewResponse,
  TokenListResponse,
  TokenQueryParams,
  AttemptListResponse,
  AttemptQueryParams,
  AppStatusResponse,
} from '../types/api';

// Helper to get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('admin_token');
};

// Build query string from params object
const buildQueryString = (params: TokenQueryParams | AttemptQueryParams): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api/admin',
  prepareHeaders: (headers) => {
    const token = getAuthToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/**
 * Admin tokens live 15 minutes. These pages used to read `admin_token` raw, so
 * once it aged out Overview, Apps Status, Push Tokens and Delivery Attempts
 * showed "Failed to load" for good (the 30s/60s polling only repeated the
 * 401). On a 401 the token is renewed from the Central session — the same
 * recovery the directory pages use — and the request is retried ONCE. A null
 * recovery means the browser is already on its way to sign-in.
 */
export const adminBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const first = await rawBaseQuery(args, api, extraOptions);
  if (first.error?.status !== 401) return first;
  const renewed = await recoverAdminSession();
  if (!renewed) return first;
  return rawBaseQuery(args, api, extraOptions);
};

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: adminBaseQuery,
  tagTypes: ['Overview', 'Tokens', 'Attempts', 'AppsStatus'],
  endpoints: (builder) => ({
    // System Overview
    getSystemOverview: builder.query<SystemOverviewResponse, void>({
      query: () => '/system/overview',
      providesTags: ['Overview'],
    }),

    // Push Tokens
    getTokens: builder.query<TokenListResponse, TokenQueryParams>({
      query: (params) => `/push/tokens${buildQueryString(params)}`,
      providesTags: ['Tokens'],
    }),

    // Delivery Attempts
    getAttempts: builder.query<AttemptListResponse, AttemptQueryParams>({
      query: (params) => `/push/attempts${buildQueryString(params)}`,
      providesTags: ['Attempts'],
    }),

    // Apps Status
    getAppsStatus: builder.query<AppStatusResponse, void>({
      query: () => '/apps/status',
      providesTags: ['AppsStatus'],
    }),
  }),
});

export const {
  useGetSystemOverviewQuery,
  useGetTokensQuery,
  useGetAttemptsQuery,
  useGetAppsStatusQuery,
} = adminApi;
